import {
  DndContext,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { cn } from '@chopchop-se/ui';
import type { Bin, WarehouseMap, WarehouseMapArea, WarehouseMapShelf } from '@warehouse/shared';
import { type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CELL_SIZE, areaTone, canvasExtent, movedRect, shelfFill, toPixels } from '../map-layout';

/**
 * The 2D grid editor (spec §5.15).
 *
 * Areas and shelves are absolutely positioned on a CSS grid background and
 * dragged with dnd-kit; the drop handler converts the pixel delta to whole grid
 * cells (`map-layout.ts`) and PATCHes the row. The map is data, not a drawing —
 * so what you drag is `warehouse_areas.x/y`, and a reload shows the same thing.
 */

export type MapSelection =
  | { kind: 'area'; id: string }
  | { kind: 'shelf'; id: string }
  | { kind: 'bin'; id: string }
  | null;

export interface MapCanvasProps {
  map: WarehouseMap;
  zoom: number;
  selection: MapSelection;
  onSelect: (selection: MapSelection) => void;
  editable: boolean;
  onMoveArea: (id: string, x: number, y: number) => void;
  onMoveShelf: (id: string, x: number, y: number) => void;
}

export function MapCanvas({
  map,
  zoom,
  selection,
  onSelect,
  editable,
  onMoveArea,
  onMoveShelf,
}: MapCanvasProps): ReactNode {
  const { t } = useTranslation('warehouseCore');
  const { cols, rows } = canvasExtent(map);
  const cell = CELL_SIZE * zoom;

  // A small activation distance keeps a click-to-select from registering as a
  // 2px drag that silently moves a shelf.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  function handleDragEnd(event: DragEndEvent): void {
    const data = event.active.data.current as
      | { kind: 'area' | 'shelf'; rect: { x: number; y: number; w: number; h: number } }
      | undefined;
    if (!data) return;
    const next = movedRect(data.rect, event.delta.x, event.delta.y, zoom);
    if (next.x === data.rect.x && next.y === data.rect.y) return;
    if (data.kind === 'area') onMoveArea(String(event.active.id).slice(5), next.x, next.y);
    else onMoveShelf(String(event.active.id).slice(6), next.x, next.y);
  }

  return (
    <div className="overflow-auto scrollbar-thin rounded-2xl border border-surface-border bg-surface p-4">
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <div
          data-testid="map-canvas"
          className="relative"
          style={{
            width: cols * cell,
            height: rows * cell,
            backgroundImage:
              'linear-gradient(to right, var(--color-surface-border) 1px, transparent 1px), linear-gradient(to bottom, var(--color-surface-border) 1px, transparent 1px)',
            backgroundSize: `${cell}px ${cell}px`,
            opacity: 1,
          }}
          onClick={() => {
            onSelect(null);
          }}
        >
          {map.areas.map((area) => (
            <AreaBox
              key={area.id}
              area={area}
              zoom={zoom}
              editable={editable}
              selected={selection?.kind === 'area' && selection.id === area.id}
              onSelect={onSelect}
            />
          ))}

          {map.areas.flatMap((area) =>
            area.shelves.map((shelf) => (
              <ShelfBox
                key={shelf.id}
                shelf={shelf}
                zoom={zoom}
                editable={editable}
                selected={selection?.kind === 'shelf' && selection.id === shelf.id}
                onSelect={onSelect}
              />
            )),
          )}

          {map.areas.flatMap((area) =>
            area.looseBins.map((bin, index) => (
              <LooseBinBox
                key={bin.id}
                bin={bin}
                area={area}
                index={index}
                zoom={zoom}
                selected={selection?.kind === 'bin' && selection.id === bin.id}
                onSelect={onSelect}
              />
            )),
          )}

          {map.areas.length === 0 ? (
            <p className="absolute inset-0 grid place-content-center text-sm text-ink-muted">
              {t('map.noAreas')}
            </p>
          ) : null}
        </div>
      </DndContext>
    </div>
  );
}

// ---------------------------------------------------------------------------

function AreaBox({
  area,
  zoom,
  editable,
  selected,
  onSelect,
}: {
  area: WarehouseMapArea;
  zoom: number;
  editable: boolean;
  selected: boolean;
  onSelect: (s: MapSelection) => void;
}): ReactNode {
  const { t } = useTranslation('warehouseCore');
  const rect = { x: area.x, y: area.y, w: area.w, h: area.h };
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `area:${area.id}`,
    disabled: !editable,
    data: { kind: 'area', rect },
  });
  const px = toPixels(rect, zoom);

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`map-area-${area.code}`}
      aria-label={`${t('map.storage')} ${area.code}`}
      onClick={(event) => {
        event.stopPropagation();
        onSelect({ kind: 'area', id: area.id });
      }}
      className={cn(
        'absolute rounded-xl border-2 border-dashed transition-shadow',
        areaTone(area),
        editable && 'cursor-grab',
        isDragging && 'z-30 cursor-grabbing opacity-80 shadow-lg',
        selected && 'ring-2 ring-brand-500 ring-offset-2 ring-offset-surface',
      )}
      style={boxStyle(px, transform)}
    >
      <span className="pointer-events-none absolute left-2 top-1.5 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-ink-light">
        {area.code} · {area.name}
      </span>
    </div>
  );
}

function ShelfBox({
  shelf,
  zoom,
  editable,
  selected,
  onSelect,
}: {
  shelf: WarehouseMapShelf;
  zoom: number;
  editable: boolean;
  selected: boolean;
  onSelect: (s: MapSelection) => void;
}): ReactNode {
  const rect = { x: shelf.x, y: shelf.y, w: shelf.w, h: shelf.h };
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `shelf:${shelf.id}`,
    disabled: !editable,
    data: { kind: 'shelf', rect },
  });
  const px = toPixels(rect, zoom);
  const fill = shelfFill(shelf);

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`map-shelf-${shelf.code}`}
      onClick={(event) => {
        event.stopPropagation();
        onSelect({ kind: 'shelf', id: shelf.id });
      }}
      className={cn(
        'absolute flex items-center justify-center overflow-hidden rounded-md border border-ink/20 bg-surface-raised text-[11px] font-medium text-ink',
        editable && 'cursor-grab',
        isDragging && 'z-30 cursor-grabbing opacity-80 shadow-lg',
        selected && 'ring-2 ring-brand-500',
      )}
      style={{ ...boxStyle(px, transform), zIndex: isDragging ? 30 : 10 }}
    >
      <span
        aria-hidden
        className="absolute inset-x-0 bottom-0 bg-brand-500/30"
        style={{ height: `${fill * 100}%` }}
      />
      <span className="relative font-mono">{shelf.code}</span>
    </div>
  );
}

/**
 * Floor bins and pillars have no coordinates of their own in the Wave-1 schema,
 * so they are laid out in a row along the bottom of their area. They are still
 * clickable map objects — a pillar is a place you cannot put a pallet, and the
 * builder has to see it.
 */
function LooseBinBox({
  bin,
  area,
  index,
  zoom,
  selected,
  onSelect,
}: {
  bin: Bin;
  area: WarehouseMapArea;
  index: number;
  zoom: number;
  selected: boolean;
  onSelect: (s: MapSelection) => void;
}): ReactNode {
  const perRow = Math.max(1, area.w);
  const rect = {
    x: area.x + (index % perRow),
    y: area.y + area.h - 1 - Math.floor(index / perRow),
    w: 1,
    h: 1,
  };
  const px = toPixels(rect, zoom);
  const isPillar = bin.kind === 'pillar';

  return (
    <button
      type="button"
      data-testid={`map-bin-${bin.code}`}
      title={bin.code}
      onClick={(event) => {
        event.stopPropagation();
        onSelect({ kind: 'bin', id: bin.id });
      }}
      className={cn(
        'absolute grid place-content-center rounded text-[10px] font-mono',
        isPillar
          ? 'border-2 border-ink/40 bg-ink/20 text-ink-muted'
          : 'border border-dashed border-ink/30 bg-surface text-ink-light',
        selected && 'ring-2 ring-brand-500',
      )}
      style={{ ...boxStyle(px, null), zIndex: 12 }}
    >
      {isPillar ? '✕' : bin.code.split('-').pop()}
    </button>
  );
}

function boxStyle(
  px: { left: number; top: number; width: number; height: number },
  transform: { x: number; y: number } | null,
): CSSProperties {
  return {
    left: px.left,
    top: px.top,
    width: px.width,
    height: px.height,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  };
}
